import React, { useState, useMemo } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, X, Loader2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Erfassung einer EXTERNEN Ausgangsrechnung (z.B. aus einem Altsystem / händisch).
 * Schreibt einen db.income-Eintrag mit source="external_invoice" inkl. Netto/USt-Split,
 * Original-Rechnungsnummer, Rechnungs- und Zahldatum sowie optionalem Beleg.
 *
 * Steuerlich: `date` = Zahldatum (UStVA/Ist-Versteuerung), `invoice_date` = Rechnungsdatum.
 */
export const ExternalInvoiceModal = ({ open, onOpenChange, countryInfo, onSaved }) => {
  const emptyForm = {
    external_invoice_number: "",
    customer_name: "",
    invoice_date: todayISO(),
    date: todayISO(),
    service_date: "",
    gross: "",
    vat_rate: countryInfo?.vat_rates?.[0]?.rate ?? 19,
    is_reverse_charge: false,
    description: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm({ ...emptyForm, vat_rate: countryInfo?.vat_rates?.[0]?.rate ?? 19 });
    setReceipt(null);
  };

  // Netto/USt aus Brutto + Satz berechnen (RC = 0% USt)
  const { net, vat } = useMemo(() => {
    const gross = parseFloat(form.gross) || 0;
    const rate = form.is_reverse_charge ? 0 : parseFloat(form.vat_rate) || 0;
    if (rate > 0) {
      const n = gross / (1 + rate / 100);
      return { net: n, vat: gross - n };
    }
    return { net: gross, vat: 0 };
  }, [form.gross, form.vat_rate, form.is_reverse_charge]);

  const fmt = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/expenses/upload-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceipt(res.data);
      toast.success("Beleg hochgeladen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const gross = parseFloat(form.gross) || 0;
    if (gross <= 0) {
      toast.error("Bitte einen Bruttobetrag größer 0 eingeben");
      return;
    }
    setSaving(true);
    try {
      const rate = form.is_reverse_charge ? 0 : parseFloat(form.vat_rate) || 0;
      const payload = {
        source: "external_invoice",
        description:
          form.description ||
          `Ausgangsrechnung ${form.external_invoice_number || ""}${form.customer_name ? " – " + form.customer_name : ""}`.trim(),
        amount: gross,
        net_amount: parseFloat(net.toFixed(2)),
        vat_amount: parseFloat(vat.toFixed(2)),
        vat_rate: rate,
        date: form.date,
        invoice_date: form.invoice_date,
        service_date: form.service_date || null,
        external_invoice_number: form.external_invoice_number || null,
        invoice_number: form.external_invoice_number || null,
        customer_name: form.customer_name || null,
        is_reverse_charge: form.is_reverse_charge,
        category: "service",
      };
      if (receipt) {
        payload.receipt_file_key = receipt.file_key;
        payload.receipt_filename = receipt.filename;
        payload.receipt_content_type = receipt.content_type;
        payload.storage_uri = receipt.storage_uri;
      }
      await api.post("/incomes", payload);
      toast.success("Externe Rechnung erfasst");
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="external-invoice-modal">
        <DialogHeader>
          <DialogTitle>Externe Ausgangsrechnung erfassen</DialogTitle>
          <DialogDescription>
            Erfasse eine Rechnung aus einem Altsystem oder eine händisch gestellte Rechnung.
            Sie fließt korrekt in deine UStVA & Finanzen ein.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rechnungsnummer</Label>
              <Input
                value={form.external_invoice_number}
                onChange={(e) => setForm({ ...form, external_invoice_number: e.target.value })}
                placeholder="RE-2025-042"
                data-testid="ext-inv-number"
              />
            </div>
            <div>
              <Label>Kunde</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="Mustermann GmbH"
                data-testid="ext-inv-customer"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rechnungsdatum</Label>
              <Input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value, date: form.date === form.invoice_date ? e.target.value : form.date })}
                data-testid="ext-inv-date"
              />
            </div>
            <div>
              <Label>Zahldatum</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                data-testid="ext-inv-paid-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">Maßgeblich für die UStVA</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Bruttobetrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.gross}
                onChange={(e) => setForm({ ...form, gross: e.target.value })}
                required
                data-testid="ext-inv-gross"
              />
            </div>
            <div>
              <Label>USt-Satz</Label>
              <Select
                value={String(form.vat_rate)}
                onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}
                disabled={form.is_reverse_charge}
              >
                <SelectTrigger data-testid="ext-inv-vat-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (steuerfrei)</SelectItem>
                  {countryInfo?.vat_rates?.map((r, i) => (
                    <SelectItem key={i} value={String(r.rate)}>{r.rate}% – {r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 p-3">
            <Checkbox
              id="ext-rc"
              checked={form.is_reverse_charge}
              onCheckedChange={(c) => setForm({ ...form, is_reverse_charge: !!c })}
              data-testid="ext-inv-rc"
            />
            <Label htmlFor="ext-rc" className="text-sm font-normal cursor-pointer">
              Reverse Charge (Steuerschuldnerschaft des Leistungsempfängers, 0% USt)
            </Label>
          </div>

          {/* Netto/USt-Vorschau */}
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="ext-inv-preview">
            <div className="flex justify-between"><span className="text-slate-600">Netto</span><span className="font-mono">{fmt(net)} €</span></div>
            <div className="flex justify-between"><span className="text-slate-600">USt ({form.is_reverse_charge ? 0 : form.vat_rate}%)</span><span className="font-mono">{fmt(vat)} €</span></div>
            <div className="flex justify-between font-semibold border-t border-emerald-200 mt-1 pt-1"><span>Brutto</span><span className="font-mono">{fmt(parseFloat(form.gross) || 0)} €</span></div>
          </div>

          <div>
            <Label>Beleg (optional)</Label>
            {receipt ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-2 text-sm" data-testid="ext-inv-receipt-attached">
                <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4 text-emerald-600" />{receipt.filename}</span>
                <button type="button" onClick={() => setReceipt(null)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50" data-testid="ext-inv-receipt-dropzone">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Lädt hoch…" : "Rechnungs-PDF/Bild anhängen"}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleUpload(e.target.files?.[0])} />
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="ext-inv-submit">
              {saving ? "Speichert…" : "Rechnung erfassen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalInvoiceModal;
